import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { Fragment, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  TPopulatedForm,
  applicationsKeyStore,
  useApplicationFormSteps,
  useCreateDraft,
  useDeclaration,
  useSubmitApplication,
} from "../../hooks/applications.hooks";
import { cleanseObject } from "../../utils/common";
import { ErrorToast } from "../toastMessage";
import {
  useSelectedApplication,
  useSetSelectedApplication,
} from "./ApplicationFrom.ctx";
import { RenderNOC } from "./RenderNOC";
import { RenderStep } from "./RenderStep";

type PropTypes = {
  defaultValues: Record<string, unknown>;
  defaultSteps: TPopulatedForm[];
};

export function BottomButtons({
  formState,
  defaults,
  getValues,
  trigger,
  serviceType,
  setDefaults,
  activeStep,
  setActiveStep,
  steps,
}: ReturnType<typeof useForm> & {
  defaults: Record<string, unknown>;
  activeStep: number;
  steps: TPopulatedForm[];
  serviceType?: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  setDefaults: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
}) {
  const { data: declaration } = useDeclaration();
  const isFetchingSteps =
    useIsFetching({
      queryKey: applicationsKeyStore.serviceFormSteps(serviceType),
      exact: true,
    }) > 0;

  const disableBtns = useIsMutating() > 0;
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const setApplication = useSetSelectedApplication();
  const application = useSelectedApplication();
  const createDraftMutation = useCreateDraft();
  const createDraftMutation1 = useCreateDraft();
  const submitMutation = useSubmitApplication();

  if (typeof serviceType !== "number" || serviceType < 0 || isFetchingSteps)
    return null;

  const getCleanData = () => {
    const cleansedObject = cleanseObject(getValues());
    return {
      ...defaults,
      ...cleansedObject,
      ...(!!cleansedObject?.documents && {
        documents: {
          ...(defaults.documents as Record<string, unknown>),
          ...(cleansedObject.documents as Record<string, unknown>),
        },
      }),
    };
  };

  const onSave = () => {
    const newDefaults = getCleanData();
    setDefaults(newDefaults);
    createDraftMutation.mutate(
      { data: newDefaults, id: application?.id },
      {
        onSuccess: (result) =>
          result.data?.data?.applicationId && setApplication(result.data.data),
      }
    );
  };

  const onSubmit = async () => {
    try {
      if (!formState.isValid)
        return trigger(undefined, { shouldFocus: true }).then(() => null);
      const cleanData = getCleanData();
      setDefaults(cleanData);
      const result = await createDraftMutation.mutateAsync({
        data: cleanData,
        id: application?.id,
      });

      if (!result.data?.data?.applicationId) return;
      setApplication(result.data.data);

      const result2 = await submitMutation.mutateAsync({
        id: result.data.data.id,
      });
      if (!result2.data.success) throw new Error();

      return navigate("/user-applications");
    } catch (err) {
      //no-op
    }
  };

  if (activeStep === steps.length - 1) {
    if (!declaration)
      return (
        <div className="flex justify-center space-x-4 items-center mt-8">
          <p className="text-base">
            Cannot submit application! Declaration misconfigured.
          </p>
        </div>
      );
    return (
      <div className="max-w-[1000px] mx-auto mt-8">
        <p className="font-semibold text-lg text-grey-600 mb-2">Declaration</p>
        <div className="flex-wrap sm:flex-nowrap flex gap-4 items-center">
          <div className="basis-full sm:basis-3/4 flex items-center">
            <input
              type="checkbox"
              checked={checked}
              className="w-auto mr-4 shrink-0 basis-[15px]"
              onChange={(e) => setChecked(e.target.checked)}
            />
            <p className="grow text-base">{declaration}</p>
          </div>
          <div className="basis-full sm:basis-1/4 space-y-2">
            <button
              type="button"
              onClick={onSave}
              disabled={disableBtns}
              className="block w-full bg-primary px-6 py-3 rounded font-bold text-white"
            >
              {createDraftMutation.isPending ? "Saving" : "Save"}
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={disableBtns || !checked}
              className="block w-full bg-green-600 px-6 py-3 rounded font-bold text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {submitMutation.isPending ? "Submitting" : "Submit"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className=" flex-wrap sm:flex-nowrap flex justify-center gap-2 sm:gap-4 items-center mt-8">
      {activeStep > 0 && (
        <button
          type="button"
          disabled={disableBtns}
          onClick={() => setActiveStep((e) => e - 1)}
          className="bg-black px-4 sm:px-6 py-2 rounded-full font-bold text-white"
        >
          Back
        </button>
      )}
      <button
        type="button"
        onClick={onSave}
        disabled={disableBtns}
        className="bg-primary px-4 sm:px-6 py-2 rounded-full font-bold text-white"
      >
        {createDraftMutation.isPending ? "Saving" : "Save as draft"}
      </button>

      <button
        type="button"
        disabled={disableBtns}
        onClick={() => {
          if (!formState.isValid)
            return trigger(undefined, { shouldFocus: true }).then(() => null);

          setDefaults(getCleanData());
          setActiveStep((e) => e + 1);
        }}
        className="bg-primary px-4 sm:px-6 py-2 rounded-full font-bold text-white"
      >
        Next
      </button>
    </div>
  );
}

export function ApplicationForm({ defaultValues, defaultSteps }: PropTypes) {
  const [serviceType, setServiceType] = useState(
    Number(defaultValues?.name_of_service) || undefined
  );
  const { data, isError, isFetching } = useApplicationFormSteps(serviceType);
  const [defaults, setDefaults] = useState({ ...defaultValues });
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState(defaultSteps);

  useEffect(() => {
    if (isFetching) return;
    console.log("step", steps)
    if (serviceType && data?.data?.length) setSteps(data.data);
    else if (serviceType && (isError || !data?.data?.length)) {
      setServiceType(undefined);
      setSteps(defaultSteps);
      ErrorToast("Service Unavailable!");
    }
  }, [isError, serviceType, data, isFetching]);

  const renderButtons = (formMethods: ReturnType<typeof useForm>) => (
    <BottomButtons
      {...formMethods}
      steps={steps}
      defaults={defaults}
      activeStep={activeStep}
      serviceType={serviceType}
      setActiveStep={setActiveStep}
      setDefaults={setDefaults}
    />
  );

  let toRender = null;

  if (steps[activeStep].type === "NOC") {
    toRender = (
      <RenderNOC
        defaultValues={defaults}
        step={steps[activeStep]}
        key={`form-step-${steps[activeStep]?.id}`}
      >
        {renderButtons}
      </RenderNOC>
    );
  } else {
    toRender = (
      <RenderStep
        defaultValues={defaults}
        step={steps[activeStep]}
        key={`form-step-${steps[activeStep]?.id}`}
        onChangeService={setServiceType}
      >
        {renderButtons}
      </RenderStep>
    );
  }

  return (
    <div className="px-2 pb-2 md:p-4 overflow-auto">
      <div className="bg-white rounded-xl shadow-lg border border-gray-300 p-8">
        <p className="text-center mb-8 text-grey-600 text-2xl font-bold">
          Application Details
        </p>
        <div className="flex justify-center mb-8 max-w-[550px] mx-auto">
          {steps.map((step, i) => (
            <Fragment key={`step-${step.id}`}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-[20px] h-[20px] box-content p-1 rounded-full border-4 ${
                    i <= activeStep ? "border-primary" : "border-gray-400"
                  }`}
                >
                  <div
                    className={`w-[20px] box-content h-[20px] rounded-full ${
                      i <= activeStep ? "bg-primary" : "bg-gray-400"
                    }`}
                  ></div>
                </div>
                <p className="text-base">{`Step ${i + 1}`}</p>
              </div>

              {i !== steps.length - 1 && (
                <hr
                  className={`grow mt-[16px] border-4 ${
                    i < activeStep ? "border-primary" : ""
                  }`}
                />
              )}
            </Fragment>
          ))}
        </div>
        {toRender}
      </div>
    </div>
  );
}
